import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card, CardBody, Col, Row, Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Input, Label, FormGroup, Table, Modal, ModalHeader, ModalBody, ModalFooter,
  Collapse,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import "./tenancy.css"
import {
  getTenantCompany, createTenantCompany, updateTenantCompany,
  getCompanyGroups, getCompanySettings, saveCompanySettings, uploadCompanyLogos,
  getCompanyNumbering, saveCompanyNumbering, suggestCompanyCode,
  getCompanyDocuments, saveCompanyDocuments,
  getCompanyBankAccounts, createCompanyBankAccount, updateCompanyBankAccount, deleteCompanyBankAccount,
} from "../../services/tenancy"
import PaymentTermsSearch from "../Settings/payTerms"
import PaymentTermsTemplateSearch from "../Settings/payTermTemplate"
import { getSponsoredList } from "../../services/Settings/dropdown"
import {
  getTrades, setTradeRate, getTradeHistory,
  getCompanyAttendanceCodes, setAttendanceOverride, getAttendanceOverrideHistory,
} from "../../services/admin"

const IMG_BASE = process.env.REACT_APP_IMG_APIKEY

// Live preview of a generated number for a numbering row (sample FY 2526, seq 1)
const numPreview = (r, companyCode = "C001") => {
  const num = "1".padStart(Number(r.no_of_digits) || 5, "0")
  const fy = r.fy_segment ? "2526" : ""
  const sep = r.company_code_separator || "-"
  const p = r.prefix || "PFX"
  if (r.numbering_method === "manual") return "Manual"
  if (r.numbering_method === "none") return "—"
  if (r.include_company_code) return [p, companyCode, fy, num].filter(Boolean).join(sep)
  return `${p}${fy}${num}`
}

const ACCOUNTING_KEYS = [
  "finance.bill_wise_details", "finance.cost_center_enabled", "finance.multiple_cost_category",
  "finance.interest_calculation", "finance.vat_enabled", "finance.vat_rate",
]

const empty = {
  code: "", company_name: "", group_id: "", trn_number: "", ct_number: "", mol_number: "",
  is_payroll: false, is_billing: false, is_vehicle_owner: false, is_active: true,
  bank_name: "", bank_account_name: "", bank_account_no: "", bank_employer_id: "", bank_sta_code: "",
  vat_rate: "", absent_deduction_per_day: "", payroll_absent_forgive_days: "", invoice_absent_forgive_days: "",
  invoice_prefix: "", invoice_next_seq: "", address: "", phone_number: "", email: "",
  signatory_name: "", signatory_title: "", sponsors: [],
}

const CompanyEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id
  document.title = (isEdit ? "Edit" : "New") + " Company | Everrise"

  const [tab, setTab] = useState("details")
  const [form, setForm] = useState(empty)
  const [logos, setLogos] = useState({}) // { logo|header_logo|watermark_logo: File }
  const [numRows, setNumRows] = useState([])
  const [codeEdited, setCodeEdited] = useState(false) // user manually changed the auto code
  const [groups, setGroups] = useState([])

  // settings
  const [settingsRows, setSettingsRows] = useState([])
  const [settingsCats, setSettingsCats] = useState({})
  const [settingsSub, setSettingsSub] = useState("accounting")
  // trades
  const [trades, setTrades] = useState([])
  const [rateInputs, setRateInputs] = useState({})
  const [historyModal, setHistoryModal] = useState(false)
  const [historyRows, setHistoryRows] = useState([])
  const [historyTrade, setHistoryTrade] = useState(null)
  // attendance
  const [attCodes, setAttCodes] = useState([])
  const [attInputs, setAttInputs] = useState({}) // {codeId: {is_paid, deduction_type}}
  const [attHistModal, setAttHistModal] = useState(false)
  const [attHistRows, setAttHistRows] = useState([])
  const [attHistCode, setAttHistCode] = useState(null)
  // documents (Documents Manager 'company' master)
  const [docTypes, setDocTypes] = useState([])
  const [docValues, setDocValues] = useState({}) // {typeId: {fieldKey: value}}
  const [docFiles, setDocFiles] = useState({})   // {`${typeId}_${fieldKey}`: File}
  const [docSaving, setDocSaving] = useState(false)
  // bank accounts (mirrored to Finance)
  const emptyBank = { bank_name: "", account_name: "", account_no: "", iban: "", swift: "", branch: "", currency: "AED", is_primary: false }
  const [banks, setBanks] = useState([])
  const [bankForm, setBankForm] = useState(emptyBank)
  const [bankEditId, setBankEditId] = useState(null)
  const [bankSaving, setBankSaving] = useState(false)
  const [bankFormOpen, setBankFormOpen] = useState(false)
  const [paySub, setPaySub] = useState("terms")

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Auto-suggest a 3-4 char code from the name on new companies, unless the user typed one.
  const suggestCode = async () => {
    if (isEdit || codeEdited || !form.company_name?.trim()) return
    try { const r = await suggestCompanyCode(form.company_name); if (r?.code) set("code", r.code) } catch (e) { /* non-fatal */ }
  }

  useEffect(() => {
    ;(async () => {
      try {
        const g = await getCompanyGroups()
        setGroups(g?.data || [])
        if (isEdit) {
          const c = await getTenantCompany(id)
          const d = c?.data
          const sponsors = (d?.sponsors || []).map(s => ({
            sponsor_id: s.id, name: s.sponsored_by || "",
            sponsor_type: s.sponsor_type || "individual", nationality: s.nationality || "",
            country: s.country || "", emirate: s.emirate || "", address: s.address || "",
            national_id: s.national_id || "", national_id_expiry: s.national_id_expiry || "",
            passport_no: s.passport_no || "", passport_expiry: s.passport_expiry || "",
            contact_phone: s.contact_phone || "", contact_email: s.contact_email || "", notes: s.notes || "",
            share_percent: s.pivot?.share_percent ?? "", role: s.pivot?.role ?? "",
          }))
          setForm({ ...empty, ...d, group_id: d?.group_id || "", sponsors })
        }
      } catch (e) { toastError(e?.message || "Failed to load") }
    })()
  }, [id, isEdit])

  const loadSettings = useCallback(async () => {
    if (!isEdit) return
    try {
      const res = await getCompanySettings(id)
      setSettingsRows((res?.settings || []).map(s => ({ ...s, input: s.own_value === null || s.own_value === undefined ? "" : String(s.own_value) })))
      setSettingsCats(res?.categories || {})
    } catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const loadTrades = useCallback(async () => {
    if (!isEdit) return
    try { const r = await getTrades(id); setTrades(r?.data || []) } catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const loadAtt = useCallback(async () => {
    if (!isEdit) return
    try { const r = await getCompanyAttendanceCodes(id); setAttCodes(r?.data || []) } catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const loadNumbering = useCallback(async () => {
    if (!isEdit) return
    try { const r = await getCompanyNumbering(id); setNumRows((r?.data?.rows || []).map(x => ({ ...x, inherit: !x.has_override }))) }
    catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const loadDocuments = useCallback(async () => {
    if (!isEdit) return
    try {
      const r = await getCompanyDocuments(id)
      setDocTypes(r?.types || [])
      const seeded = {}
      Object.entries(r?.records || {}).forEach(([typeId, rec]) => { seeded[typeId] = rec?.values || {} })
      setDocValues(seeded)
      setDocFiles({})
    } catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const loadBanks = useCallback(async () => {
    if (!isEdit) return
    try { const r = await getCompanyBankAccounts(id); setBanks(r?.data || []) } catch (e) { toastError(e?.message) }
  }, [id, isEdit])

  const editBank = b => { setBankEditId(b.id); setBankForm({ ...emptyBank, ...b, is_primary: !!b.is_primary }); setBankFormOpen(true) }
  const resetBank = () => { setBankEditId(null); setBankForm(emptyBank); setBankFormOpen(false) }
  const saveBank = async () => {
    if (!bankForm.bank_name?.trim()) return toastError("Bank name is required")
    try {
      setBankSaving(true)
      if (bankEditId) await updateCompanyBankAccount(id, bankEditId, bankForm)
      else await createCompanyBankAccount(id, bankForm)
      toastSuccess(bankEditId ? "Bank account updated" : "Bank account added")
      resetBank(); loadBanks()
    } catch (e) { toastError(e?.message) } finally { setBankSaving(false) }
  }
  const removeBank = async b => {
    try { await deleteCompanyBankAccount(id, b.id); toastSuccess("Bank account removed"); loadBanks() }
    catch (e) { toastError(e?.message) }
  }

  useEffect(() => {
    if (tab === "settings") loadSettings()
    if (tab === "trades") loadTrades()
    if (tab === "attendance") loadAtt()
    if (tab === "numbering") loadNumbering()
    if (tab === "details") loadDocuments()
    if (tab === "banks") loadBanks()
  }, [tab, loadSettings, loadTrades, loadAtt, loadNumbering, loadDocuments, loadBanks])

  const setDocValue = (typeId, fieldKey, value) =>
    setDocValues(s => ({ ...s, [typeId]: { ...(s[typeId] || {}), [fieldKey]: value } }))

  const saveDocuments = async () => {
    // Required-field guard
    for (const t of docTypes) {
      for (const f of t.fields || []) {
        if (f.is_required && f.type !== "file" && !((docValues[t.id] || {})[f.key] || "").toString().trim()) {
          return toastError(`${t.name}: ${f.label} is required`)
        }
      }
    }
    try {
      setDocSaving(true)
      const fd = new FormData()
      fd.append("data", JSON.stringify(docTypes.map(t => ({ document_type_id: t.id, values: docValues[t.id] || {} }))))
      Object.entries(docFiles).forEach(([k, file]) => { if (file) fd.append(`file_${k}`, file) })
      const r = await saveCompanyDocuments(id, fd)
      toastSuccess(r?.message || "Documents saved")
      loadDocuments()
    } catch (e) { toastError(e?.message) } finally { setDocSaving(false) }
  }

  // sponsors — the company only LINKS an existing sponsor (managed in Administration →
  // Sponsors) with a share % / role. The full sponsor form lives in that master.
  const removeSponsor = i => setForm(f => ({ ...f, sponsors: f.sponsors.filter((_, idx) => idx !== i) }))
  const sponsorTotal = (form.sponsors || []).reduce((t, s) => t + (parseFloat(s.share_percent) || 0), 0)
  const setSponsor = (i, k, v) => setForm(f => ({ ...f, sponsors: f.sponsors.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }))
  const [sponsorOptions, setSponsorOptions] = useState([])
  const [sponsorPick, setSponsorPick] = useState({ sponsor_id: "", share_percent: "", role: "" })
  useEffect(() => {
    ;(async () => { try { const r = await getSponsoredList(); setSponsorOptions(r?.data || []) } catch (e) { /* non-fatal */ } })()
  }, [])
  const addLinkedSponsor = () => {
    if (!sponsorPick.sponsor_id) return toastError("Pick a sponsor")
    if ((form.sponsors || []).some(s => String(s.sponsor_id) === String(sponsorPick.sponsor_id))) return toastError("Sponsor already linked")
    const opt = sponsorOptions.find(o => String(o.id) === String(sponsorPick.sponsor_id))
    setForm(f => ({ ...f, sponsors: [...(f.sponsors || []), {
      sponsor_id: Number(sponsorPick.sponsor_id), name: opt?.sponsored_by || "",
      sponsor_type: opt?.sponsor_type, nationality: opt?.nationality,
      share_percent: sponsorPick.share_percent, role: sponsorPick.role,
    }] }))
    setSponsorPick({ sponsor_id: "", share_percent: "", role: "" })
  }

  const save = async () => {
    try {
      const num = v => (v === "" || v === null || v === undefined ? null : v)
      const payload = {
        ...form, group_id: form.group_id || null,
        vat_rate: num(form.vat_rate), absent_deduction_per_day: num(form.absent_deduction_per_day),
        payroll_absent_forgive_days: num(form.payroll_absent_forgive_days), invoice_absent_forgive_days: num(form.invoice_absent_forgive_days),
        invoice_next_seq: num(form.invoice_next_seq),
        sponsors: (form.sponsors || []).filter(s => s.sponsor_id || s.name),
      }
      const res = isEdit
        ? await updateTenantCompany(id, payload)
        : await createTenantCompany(payload)
      const savedId = isEdit ? id : res?.data?.id
      // upload any selected logos in a second multipart call
      if (savedId && Object.keys(logos).length) {
        const fd = new FormData()
        Object.entries(logos).forEach(([k, file]) => fd.append(k, file))
        await uploadCompanyLogos(savedId, fd)
      }
      toastSuccess("Company saved")
      navigate("/company-management")
    } catch (e) { toastError(e?.message || (e?.errors && Object.values(e.errors)[0][0]) || "Save failed") }
  }

  // settings save
  const setSettingInput = (key, val) => setSettingsRows(rows => rows.map(r => (r.key === key ? { ...r, input: val } : r)))
  const saveSettings = async () => {
    try {
      const payload = {}; settingsRows.forEach(r => { payload[r.key] = r.input })
      await saveCompanySettings(id, payload); toastSuccess("Settings saved"); loadSettings()
    } catch (e) { toastError(e?.message || "Save failed") }
  }
  const renderSettingRow = r => (
    <Row key={r.key} className="align-items-center mb-2">
      <Col md={5}><Label className="mb-0">{r.label}{r.unit ? ` (${r.unit})` : ""}</Label>{r.help && <div className="text-muted small">{r.help}</div>}</Col>
      <Col md={5}>
        {r.type === "boolean" ? (
          <Input type="select" value={r.input} onChange={e => setSettingInput(r.key, e.target.value)}>
            <option value="">Inherit ({String(r.inherited_value)})</option><option value="1">Yes</option><option value="0">No</option>
          </Input>
        ) : r.type === "select" ? (
          <Input type="select" value={r.input} onChange={e => setSettingInput(r.key, e.target.value)}>
            <option value="">Inherit ({String(r.inherited_value)})</option>
            {(r.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Input>
        ) : (
          <Input value={r.input} placeholder={`Inherit (${String(r.inherited_value)})`} onChange={e => setSettingInput(r.key, e.target.value)} />
        )}
      </Col>
    </Row>
  )

  // trade rate save
  const saveRate = async tradeId => {
    try {
      await setTradeRate(tradeId, { scope: "company", scope_id: Number(id), hourly_rate: rateInputs[tradeId], effective_from: null })
      toastSuccess("Rate set"); loadTrades()
    } catch (e) { toastError(e?.message || "Failed") }
  }
  const openHistory = async t => {
    try {
      const r = await getTradeHistory(t.id, "company", id)
      setHistoryRows(r?.data || []); setHistoryTrade(t); setHistoryModal(true)
    } catch (e) { toastError(e?.message || "Failed") }
  }

  // attendance override
  const setAtt = (codeId, k, v) => setAttInputs(s => ({ ...s, [codeId]: { ...(s[codeId] || {}), [k]: v } }))
  const saveAttOverride = async codeId => {
    try {
      const inp = attInputs[codeId] || {}
      const payload = { scope: "company", scope_id: Number(id), effective_from: null }
      if (inp.is_paid === "1" || inp.is_paid === "0") payload.is_paid = inp.is_paid === "1"
      if (inp.deduction_type) payload.deduction_type = inp.deduction_type
      if (payload.is_paid === undefined && !payload.deduction_type) { toastError("Set a value to override"); return }
      await setAttendanceOverride(codeId, payload)
      toastSuccess("Override set"); loadAtt(); setAttInputs(s => ({ ...s, [codeId]: {} }))
    } catch (e) { toastError(e?.message || "Failed") }
  }
  // numbering
  const setNumRow = (vtId, k, v) => setNumRows(rows => rows.map(r => r.voucher_type_id === vtId ? { ...r, [k]: v } : r))
  // editing any numbering field makes it a company override (auto-uncheck Inherit)
  const editNum = (vtId, k, v) => setNumRows(rows => rows.map(r => r.voucher_type_id === vtId ? { ...r, [k]: v, inherit: false } : r))
  const saveNumbering = async () => {
    try {
      await saveCompanyNumbering(id, numRows.map(r => ({
        voucher_type_id: r.voucher_type_id, inherit: !!r.inherit,
        prefix: r.prefix, no_of_digits: r.no_of_digits, fy_segment: r.fy_segment, yearly_reset: r.yearly_reset,
        include_company_code: r.include_company_code, company_code_separator: r.company_code_separator, numbering_method: r.numbering_method,
      })))
      toastSuccess("Numbering saved"); loadNumbering()
    } catch (e) { toastError(e?.message || "Failed") }
  }

  const openAttHistory = async c => {
    try {
      const r = await getAttendanceOverrideHistory(c.id, "company", id)
      setAttHistRows(r?.data || []); setAttHistCode(c); setAttHistModal(true)
    } catch (e) { toastError(e?.message || "Failed") }
  }

  return (
    <div className="page-content tnt-scope">
      {/* Breadcrumb + primary action on the grey panel (outside the white card),
          matching the listing-page header layout. */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Breadcrumbs
          title="Group / Company"
          breadcrumbObject={[
            { title: "Administration", link: "#" },
            { title: "Group / Company", link: "/company-management" },
            { title: isEdit ? `Edit Company${form.company_name ? " — " + form.company_name : ""}` : "New Company", link: "#" },
          ]}
        />
        <Button color="primary" onClick={save}>{isEdit ? "Save changes" : "Create company"}</Button>
      </div>
      {/* Outer rounded body panel so the page is framed like the rest of the app. */}
      <Card className="tnt-page-card me-2 px-2 pb-2 main-dashboard-bg">
        <CardBody>

      <Nav tabs className="mb-3">
        {["details", "settings", "payment", "banks", "numbering", "trades", "attendance"].map(t => (
          <NavItem key={t}>
            <NavLink className={tab === t ? "active" : ""} style={{ cursor: "pointer" }}
              onClick={() => (t !== "details" && !isEdit) ? toastError("Save the company first") : setTab(t)}>
              {t === "details" ? "Details" : t === "settings" ? "Default settings" : t === "payment" ? "Payment Settings" : t === "banks" ? "Bank Accounts" : t === "numbering" ? "Voucher numbering" : t === "trades" ? "Trade rates" : "Attendance rules"}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={tab}>
        {/* DETAILS */}
        <TabPane tabId="details">
          <Card className="mb-3"><CardBody>
            <h6 className="text-uppercase fw-bold mb-3">Identity</h6>
            <Row>
              <Col md={2}><FormGroup><Label>Code *</Label><Input value={form.code} onChange={e => { setCodeEdited(true); set("code", e.target.value.toUpperCase()) }} /><small className="text-muted">Auto; editable</small></FormGroup></Col>
              <Col md={4}><FormGroup><Label>Legal name *</Label><Input value={form.company_name} onChange={e => set("company_name", e.target.value)} onBlur={suggestCode} /></FormGroup></Col>
              <Col md={2}><FormGroup><Label>MOL no.</Label><Input value={form.mol_number || ""} onChange={e => set("mol_number", e.target.value)} /></FormGroup></Col>
              <Col md={2}><FormGroup><Label>TRN no.</Label><Input value={form.trn_number || ""} onChange={e => set("trn_number", e.target.value)} /></FormGroup></Col>
              <Col md={2}><FormGroup><Label>CT no.</Label><Input value={form.ct_number || ""} onChange={e => set("ct_number", e.target.value)} /><small className="text-muted">Corporate tax</small></FormGroup></Col>
              <Col md={3}><FormGroup><Label>Runs payroll</Label><Input type="select" value={form.is_payroll ? "1" : "0"} onChange={e => set("is_payroll", e.target.value === "1")}><option value="1">Yes</option><option value="0">No</option></Input></FormGroup></Col>
              <Col md={3}><FormGroup><Label>Issues invoices</Label><Input type="select" value={form.is_billing ? "1" : "0"} onChange={e => set("is_billing", e.target.value === "1")}><option value="1">Yes</option><option value="0">No</option></Input></FormGroup></Col>
              <Col md={3}><FormGroup><Label>Owns vehicles</Label><Input type="select" value={form.is_vehicle_owner ? "1" : "0"} onChange={e => set("is_vehicle_owner", e.target.value === "1")}><option value="1">Yes</option><option value="0">No</option></Input></FormGroup></Col>
              <Col md={3}><FormGroup><Label>Active</Label><Input type="select" value={form.is_active ? "1" : "0"} onChange={e => set("is_active", e.target.value === "1")}><option value="1">Yes</option><option value="0">No</option></Input></FormGroup></Col>
            </Row>

          </CardBody></Card>
          <Card className="mb-3"><CardBody>
            <h6 className="text-uppercase fw-bold mb-3">Group</h6>
            <Input type="select" value={form.group_id} onChange={e => set("group_id", e.target.value)} style={{ maxWidth: 420 }}>
              <option value="">Standalone</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Input>

          </CardBody></Card>
          <Card className="mb-3"><CardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-uppercase fw-bold mb-0">Shareholders / Sponsors</h6>
              <a href="/admin/sponsors" target="_blank" rel="noreferrer" className="small">Manage sponsors →</a>
            </div>
            <Table responsive borderless className="align-middle tnt-table mb-2">
              <thead><tr><th>Name</th><th>Type</th><th>Nationality</th><th style={{ width: 110 }}>Share %</th><th style={{ width: 160 }}>Role</th><th></th></tr></thead>
              <tbody>
                {(form.sponsors || []).map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td className="text-capitalize">{s.sponsor_type || "individual"}</td>
                    <td>{s.nationality || "—"}</td>
                    <td><Input bsSize="sm" value={s.share_percent} onChange={e => setSponsor(i, "share_percent", e.target.value)} /></td>
                    <td><Input bsSize="sm" value={s.role || ""} onChange={e => setSponsor(i, "role", e.target.value)} /></td>
                    <td className="text-end text-nowrap">
                      <i className="uil-trash-alt action-icon danger" role="button" title="Remove" onClick={() => removeSponsor(i)} />
                    </td>
                  </tr>
                ))}
                {!(form.sponsors || []).length && <tr><td colSpan="6" className="text-muted">No sponsors linked.</td></tr>}
              </tbody>
            </Table>
            {/* Short link form — pick a sponsor from the master + share % / role */}
            <Row className="align-items-end g-2">
              <Col md={5}>
                <Label className="mb-0 small">Add sponsor</Label>
                <Input type="select" bsSize="sm" value={sponsorPick.sponsor_id} onChange={e => setSponsorPick(p => ({ ...p, sponsor_id: e.target.value }))}>
                  <option value="">Select a sponsor…</option>
                  {sponsorOptions.map(o => <option key={o.id} value={o.id}>{o.sponsored_by}</option>)}
                </Input>
              </Col>
              <Col md={2}><Label className="mb-0 small">Share %</Label><Input bsSize="sm" value={sponsorPick.share_percent} onChange={e => setSponsorPick(p => ({ ...p, share_percent: e.target.value }))} /></Col>
              <Col md={3}><Label className="mb-0 small">Role</Label><Input bsSize="sm" placeholder="e.g. Local Partner" value={sponsorPick.role} onChange={e => setSponsorPick(p => ({ ...p, role: e.target.value }))} /></Col>
              <Col md={2}><Button size="sm" color="light" className="w-100" onClick={addLinkedSponsor}>+ Link</Button></Col>
            </Row>
            <div className="text-end mt-2">
              <small className={sponsorTotal === 100 || sponsorTotal === 0 ? "text-muted" : "text-warning"}>Total: {sponsorTotal}%</small>
            </div>

          </CardBody></Card>
          {/* Banking moved to finance: bank accounts are created as ledgers under the
              "Bank Account" group, so the single bank fields are removed from here. */}
          <Card className="mb-3"><CardBody>
            <h6 className="text-uppercase fw-bold mb-3">Contact &amp; Signatory</h6>
            <Row>
              <Col md={12}><FormGroup><Label>Address</Label><Input type="textarea" rows={2} value={form.address || ""} onChange={e => set("address", e.target.value)} /></FormGroup></Col>
              <Col md={6}><FormGroup><Label>Phone</Label><Input value={form.phone_number || ""} onChange={e => set("phone_number", e.target.value)} /></FormGroup></Col>
              <Col md={6}><FormGroup><Label>Email</Label><Input value={form.email || ""} onChange={e => set("email", e.target.value)} /></FormGroup></Col>
              <Col md={6}><FormGroup><Label>Signatory name</Label><Input value={form.signatory_name || ""} onChange={e => set("signatory_name", e.target.value)} /></FormGroup></Col>
              <Col md={6}><FormGroup><Label>Signatory title</Label><Input value={form.signatory_title || ""} onChange={e => set("signatory_title", e.target.value)} /></FormGroup></Col>
            </Row>
            <h6 className="mt-3 mb-2 text-muted">Branding (used on printed documents)</h6>
            <Row>
              {[
                { key: "logo", label: "Company logo" },
                { key: "header_logo", label: "Header logo (print top)" },
                { key: "watermark_logo", label: "Watermark logo" },
              ].map(({ key, label }) => (
                <Col md={4} key={key}>
                  <FormGroup>
                    <Label>{label}</Label>
                    <Input type="file" accept="image/*" onChange={e => setLogos(l => ({ ...l, [key]: e.target.files[0] }))} />
                    {(logos[key] || form[key]) && (
                      <div className="mt-2">
                        <img
                          src={logos[key] ? URL.createObjectURL(logos[key]) : `${IMG_BASE}${form[key]}`}
                          alt={label}
                          style={{ maxHeight: 56, maxWidth: 140, objectFit: "contain", border: "1px solid #eef0f4", borderRadius: 6, padding: 4 }}
                        />
                      </div>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </CardBody></Card>
          {/* DOCUMENTS — types assigned to the 'company' master in the Documents Manager */}
          {isEdit && (
          <Card className="mb-3"><CardBody>
            <h6 className="text-uppercase fw-bold mb-3">Documents</h6>
            {!docTypes.length && (
              <div className="text-muted small">
                No document types are assigned to the Company master yet. Add one in the Documents Manager and assign it to “Company”.
              </div>
            )}
            {docTypes.map(t => (
              <Card key={t.id} className="mb-3 border">
                <CardBody>
                  <h6 className="mb-1">{t.name}</h6>
                  {t.description ? <div className="text-muted small mb-3">{t.description}</div> : null}
                  <Row>
                    {(t.fields || []).map(f => {
                      const val = (docValues[t.id] || {})[f.key] ?? ""
                      const label = (
                        <Label className="mb-1">
                          {f.label}{f.is_required ? <span className="mandatory">*</span> : null}
                        </Label>
                      )
                      if (f.type === "file") {
                        return (
                          <Col md={6} key={f.key} className="mb-3">
                            {label}
                            <Input type="file" bsSize="sm" onChange={e => setDocFiles(s => ({ ...s, [`${t.id}_${f.key}`]: e.target.files?.[0] || null }))} />
                            {val ? <div className="small mt-1"><a href={`${IMG_BASE}${val}`} target="_blank" rel="noreferrer">View current</a></div> : null}
                          </Col>
                        )
                      }
                      if (f.type === "select") {
                        return (
                          <Col md={6} key={f.key} className="mb-3">
                            {label}
                            <Input type="select" bsSize="sm" value={val} onChange={e => setDocValue(t.id, f.key, e.target.value)}>
                              <option value="">Select</option>
                              {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                            </Input>
                          </Col>
                        )
                      }
                      if (f.type === "textarea") {
                        return (
                          <Col md={12} key={f.key} className="mb-3">
                            {label}
                            <Input type="textarea" bsSize="sm" value={val} onChange={e => setDocValue(t.id, f.key, e.target.value)} />
                          </Col>
                        )
                      }
                      return (
                        <Col md={6} key={f.key} className="mb-3">
                          {label}
                          <Input
                            type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                            bsSize="sm"
                            value={val}
                            placeholder={f.help || ""}
                            onChange={e => setDocValue(t.id, f.key, e.target.value)}
                          />
                        </Col>
                      )
                    })}
                  </Row>
                </CardBody>
              </Card>
            ))}
            {docTypes.length ? (
              <div className="d-flex justify-content-end">
                <Button color="primary" size="sm" onClick={saveDocuments} disabled={docSaving}>
                  {docSaving ? "Saving…" : "Save documents"}
                </Button>
              </div>
            ) : null}
          </CardBody></Card>
          )}
        </TabPane>

        {/* DEFAULT SETTINGS */}
        <TabPane tabId="settings">
          <Card><CardBody>
            {(() => {
              // Group settings by their definition category (Finance, Payroll settings,
              // Localization, …) so each category is its own sub-tab.
              const cats = Object.keys(settingsCats).filter(c => settingsRows.some(r => r.category === c))
              const activeCat = cats.includes(settingsSub) ? settingsSub : cats[0]
              return (
                <>
                  <Nav tabs className="mb-3">
                    {cats.map(c => (
                      <NavItem key={c}>
                        <NavLink className={activeCat === c ? "active" : ""} style={{ cursor: "pointer" }} onClick={() => setSettingsSub(c)}>
                          {settingsCats[c] || c}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                  <p className="text-muted small">Leave a field blank to inherit from the group / global defaults.</p>
                  <TabContent activeTab={activeCat}>
                    {cats.map(c => (
                      <TabPane key={c} tabId={c}>{settingsRows.filter(r => r.category === c).map(renderSettingRow)}</TabPane>
                    ))}
                  </TabContent>
                  <div className="mt-3"><Button color="primary" onClick={saveSettings}>Save settings</Button></div>
                </>
              )
            })()}
          </CardBody></Card>
        </TabPane>

        {/* PAYMENT SETTINGS — company-scoped payment rules + payment-terms templates.
            Fields persist with the company (top "Save changes" button). The Payment
            Terms menu was retired; its templates are managed from here. */}
        <TabPane tabId="payment">
          <div className="app-tab-shell pay-tab-shell">
            <Nav tabs className="mb-3">
              <NavItem><NavLink className={paySub === "terms" ? "active" : ""} style={{ cursor: "pointer" }} onClick={() => setPaySub("terms")}>Payment Terms</NavLink></NavItem>
              <NavItem><NavLink className={paySub === "templates" ? "active" : ""} style={{ cursor: "pointer" }} onClick={() => setPaySub("templates")}>Templates</NavLink></NavItem>
            </Nav>
            <TabContent activeTab={paySub}>
              <TabPane tabId="terms">{paySub === "terms" && <PaymentTermsSearch embedded />}</TabPane>
              <TabPane tabId="templates">{paySub === "templates" && <PaymentTermsTemplateSearch embedded />}</TabPane>
            </TabContent>
          </div>
        </TabPane>

        {/* BANK ACCOUNTS — per company, mirrored into Finance as Bank-group ledgers */}
        <TabPane tabId="banks">
          <Card><CardBody>
            <p className="text-muted small">
              Bank accounts held by this company. Each account is mirrored into <b>Finance</b> as a
              ledger under the Bank account group, so it appears in the chart of accounts and can be
              used in vouchers.
            </p>
            {/* Add/Edit form — collapsible accordion at the top so the listing stays clear */}
            <Card className="border mb-3">
              <button
                type="button"
                className="btn d-flex justify-content-between align-items-center w-100 px-3 py-2"
                style={{ boxShadow: "none" }}
                onClick={() => setBankFormOpen(o => !o)}
              >
                <span className="text-uppercase fw-bold" style={{ color: "var(--accent-clr)" }}>
                  {bankEditId ? "Edit bank account" : "Add bank account"}
                </span>
                <i className={`mdi mdi-chevron-${bankFormOpen ? "up" : "down"} font-18`} style={{ color: "var(--accent-clr)" }} />
              </button>
              <Collapse isOpen={bankFormOpen}>
                <div className="px-3 pb-3">
                  <Row>
                    <Col md={4}><FormGroup><Label>Bank name<span className="mandatory">*</span></Label><Input bsSize="sm" value={bankForm.bank_name} onChange={e => setBankForm(f => ({ ...f, bank_name: e.target.value }))} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Account name</Label><Input bsSize="sm" value={bankForm.account_name} onChange={e => setBankForm(f => ({ ...f, account_name: e.target.value }))} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Account no.</Label><Input bsSize="sm" value={bankForm.account_no} onChange={e => setBankForm(f => ({ ...f, account_no: e.target.value }))} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>IBAN</Label><Input bsSize="sm" value={bankForm.iban} onChange={e => setBankForm(f => ({ ...f, iban: e.target.value }))} /></FormGroup></Col>
                    <Col md={2}><FormGroup><Label>SWIFT</Label><Input bsSize="sm" value={bankForm.swift} onChange={e => setBankForm(f => ({ ...f, swift: e.target.value }))} /></FormGroup></Col>
                    <Col md={4}><FormGroup><Label>Branch</Label><Input bsSize="sm" value={bankForm.branch} onChange={e => setBankForm(f => ({ ...f, branch: e.target.value }))} /></FormGroup></Col>
                    <Col md={2}><FormGroup><Label>Currency</Label><Input bsSize="sm" value={bankForm.currency} onChange={e => setBankForm(f => ({ ...f, currency: e.target.value }))} /></FormGroup></Col>
                    <Col md={3} className="d-flex align-items-center mt-3">
                      <Input type="checkbox" className="me-2" checked={!!bankForm.is_primary} onChange={e => setBankForm(f => ({ ...f, is_primary: e.target.checked }))} />
                      <Label className="mb-0">Primary account</Label>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end gap-2">
                    {bankEditId ? <Button color="light" size="sm" onClick={resetBank}>Cancel</Button> : null}
                    <Button color="primary" size="sm" onClick={saveBank} disabled={bankSaving}>{bankSaving ? "Saving…" : bankEditId ? "Update account" : "Add account"}</Button>
                  </div>
                </div>
              </Collapse>
            </Card>

            <Table responsive borderless className="align-middle tnt-table">
              <thead><tr>
                <th>Bank</th><th>Account name</th><th>Account no.</th><th>IBAN</th><th>Currency</th><th>Primary</th><th></th>
              </tr></thead>
              <tbody>
                {banks.map(b => (
                  <tr key={b.id}>
                    <td>{b.bank_name}{b.branch ? <div className="text-muted small">{b.branch}</div> : null}</td>
                    <td>{b.account_name}</td>
                    <td>{b.account_no}</td>
                    <td>{b.iban}</td>
                    <td>{b.currency}</td>
                    <td>{b.is_primary ? <span className="badge bg-success">Primary</span> : ""}</td>
                    <td className="text-end text-nowrap">
                      <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => editBank(b)} />
                      <i className="uil-trash-alt action-icon danger" role="button" title="Remove" onClick={() => removeBank(b)} />
                    </td>
                  </tr>
                ))}
                {!banks.length && <tr><td colSpan="7" className="text-center text-muted">No bank accounts yet.</td></tr>}
              </tbody>
            </Table>
          </CardBody></Card>
        </TabPane>

        {/* VOUCHER NUMBERING */}
        <TabPane tabId="numbering">
          <Card><CardBody>
            <p className="text-muted small">Override voucher numbering for this company. Just edit any field — “Inherit” unchecks automatically and that type becomes a company override. Re-check “Inherit” to revert it to the global template from Voucher Types.</p>
            <Table responsive borderless className="align-middle tnt-table">
              <thead><tr>
                <th>Voucher Type</th><th>Inherit</th><th>Method</th><th>Prefix</th><th>Digits</th>
                <th>FY</th><th>Reset</th><th>Co.&nbsp;code</th><th>Sep</th><th>Preview</th>
              </tr></thead>
              <tbody>
                {numRows.map(r => {
                  const dis = r.numbering_method !== "automatic"
                  return (
                    <tr key={r.voucher_type_id} className={r.inherit ? "" : "tnt-num-override"}>
                      <td>{r.voucher_type}<div className="text-muted small text-capitalize">{r.module}</div></td>
                      <td><Input type="checkbox" checked={r.inherit} onChange={e => setNumRow(r.voucher_type_id, "inherit", e.target.checked)} /></td>
                      <td><Input type="select" bsSize="sm" value={r.numbering_method} onChange={e => editNum(r.voucher_type_id, "numbering_method", e.target.value)} style={{ minWidth: 104 }}>
                        <option value="automatic">Automatic</option><option value="manual">Manual</option><option value="none">None</option>
                      </Input></td>
                      <td><Input bsSize="sm" disabled={dis} value={r.prefix} onChange={e => editNum(r.voucher_type_id, "prefix", e.target.value.toUpperCase())} style={{ width: 80 }} /></td>
                      <td><Input bsSize="sm" type="number" disabled={dis} value={r.no_of_digits} onChange={e => editNum(r.voucher_type_id, "no_of_digits", e.target.value)} style={{ width: 64 }} /></td>
                      <td><Input type="checkbox" disabled={dis} checked={r.fy_segment} onChange={e => editNum(r.voucher_type_id, "fy_segment", e.target.checked)} /></td>
                      <td><Input type="checkbox" disabled={dis} checked={r.yearly_reset} onChange={e => editNum(r.voucher_type_id, "yearly_reset", e.target.checked)} /></td>
                      <td><Input type="checkbox" disabled={dis} checked={r.include_company_code} onChange={e => editNum(r.voucher_type_id, "include_company_code", e.target.checked)} /></td>
                      <td><Input bsSize="sm" disabled={dis || !r.include_company_code} value={r.company_code_separator} onChange={e => editNum(r.voucher_type_id, "company_code_separator", e.target.value)} style={{ width: 48 }} /></td>
                      <td className="font-monospace small">{r.inherit ? <span className="text-muted">inherit</span> : numPreview(r, form.code || "C001")}</td>
                    </tr>
                  )
                })}
                {!numRows.length && <tr><td colSpan="10" className="text-center text-muted">No voucher types</td></tr>}
              </tbody>
            </Table>
            <div className="mt-3"><Button color="primary" onClick={saveNumbering}>Save numbering</Button></div>
          </CardBody></Card>
        </TabPane>

        {/* TRADE RATES */}
        <TabPane tabId="trades">
          <Card><CardBody>
            <p className="text-muted small">Override hourly rates for this company. Blank inherits the group / global / trade default. Resolved = the rate that applies to this company today.</p>
            <Table responsive borderless className="align-middle">
              <thead><tr><th>Code</th><th>Trade</th><th>Default</th><th>Resolved (this company)</th><th>Set company rate</th><th>History</th></tr></thead>
              <tbody>
                {trades.filter(t => t.salary_type === "variable").map(t => (
                  <tr key={t.id}>
                    <td>{t.code}</td><td>{t.name}</td><td>{t.default_hourly_rate ?? "—"}</td><td>{t.resolved_hourly_rate ?? "—"}</td>
                    <td>
                      <div className="d-flex" style={{ maxWidth: 240 }}>
                        <Input bsSize="sm" placeholder="rate" value={rateInputs[t.id] ?? ""} onChange={e => setRateInputs(s => ({ ...s, [t.id]: e.target.value }))} />
                        <Button size="sm" color="primary" className="ms-2" onClick={() => saveRate(t.id)}>Set</Button>
                      </div>
                    </td>
                    <td><Button color="link" size="sm" className="text-primary p-0" onClick={() => openHistory(t)}>History</Button></td>
                  </tr>
                ))}
                {!trades.length && <tr><td colSpan="6" className="text-center text-muted">No variable trades</td></tr>}
              </tbody>
            </Table>
          </CardBody></Card>
        </TabPane>

        {/* ATTENDANCE RULES */}
        <TabPane tabId="attendance">
          <Card><CardBody>
            <p className="text-muted small">Override attendance-code payroll rules for this company (effective-dated, with history). Blank = inherit from group / base code. Resolved = the rule that applies to this company today.</p>
            <Table responsive borderless className="align-middle">
              <thead><tr><th>Code</th><th>Label</th><th>Paid (base→resolved)</th><th>Deduction (base→resolved)</th><th>Override paid</th><th>Override deduction</th><th></th><th>History</th></tr></thead>
              <tbody>
                {attCodes.map(c => (
                  <tr key={c.id}>
                    <td>{c.code}</td><td>{c.label}</td>
                    <td>{String(c.base.is_paid)} → <b>{String(c.resolved.is_paid)}</b></td>
                    <td>{c.base.deduction_type || "none"} → <b>{c.resolved.deduction_type || "none"}</b></td>
                    <td style={{ minWidth: 120 }}>
                      <Input bsSize="sm" type="select" value={(attInputs[c.id] || {}).is_paid ?? ""} onChange={e => setAtt(c.id, "is_paid", e.target.value)}>
                        <option value="">Inherit</option><option value="1">Yes</option><option value="0">No</option>
                      </Input>
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <Input bsSize="sm" type="select" value={(attInputs[c.id] || {}).deduction_type ?? ""} onChange={e => setAtt(c.id, "deduction_type", e.target.value)}>
                        <option value="">Inherit</option><option value="none">none</option><option value="absent_25">absent_25</option><option value="no_salary">no_salary</option><option value="penalty">penalty</option>
                      </Input>
                    </td>
                    <td><Button size="sm" color="primary" onClick={() => saveAttOverride(c.id)}>Set</Button></td>
                    <td><Button color="link" size="sm" className="text-primary p-0" onClick={() => openAttHistory(c)}>History</Button></td>
                  </tr>
                ))}
                {!attCodes.length && <tr><td colSpan="8" className="text-center text-muted">No attendance codes</td></tr>}
              </tbody>
            </Table>
          </CardBody></Card>
        </TabPane>

      </TabContent>
        </CardBody>
      </Card>


      {/* TRADE RATE HISTORY */}
      <Modal isOpen={historyModal} toggle={() => setHistoryModal(false)} size="lg">
        <ModalHeader toggle={() => setHistoryModal(false)}>Rate history — {historyTrade?.name} (this company)</ModalHeader>
        <ModalBody>
          <Table responsive borderless className="align-middle">
            <thead><tr><th>Hourly rate</th><th>Effective from</th><th>Effective to</th><th>Status</th></tr></thead>
            <tbody>
              {historyRows.map(h => (
                <tr key={h.id}>
                  <td>{h.hourly_rate ?? "—"}</td>
                  <td>{h.effective_from ? String(h.effective_from).slice(0, 10) : "—"}</td>
                  <td>{h.effective_to ? String(h.effective_to).slice(0, 10) : <span className="text-success">current</span>}</td>
                  <td>{h.effective_to ? <span className="text-muted">closed</span> : <span className="text-success">active</span>}</td>
                </tr>
              ))}
              {!historyRows.length && <tr><td colSpan="4" className="text-center text-muted">No company-level rate history</td></tr>}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter><Button color="light" onClick={() => setHistoryModal(false)}>Close</Button></ModalFooter>
      </Modal>

      {/* ATTENDANCE OVERRIDE HISTORY */}
      <Modal isOpen={attHistModal} toggle={() => setAttHistModal(false)} size="lg">
        <ModalHeader toggle={() => setAttHistModal(false)}>Override history — {attHistCode?.code} {attHistCode?.label} (this company)</ModalHeader>
        <ModalBody>
          <Table responsive borderless className="align-middle">
            <thead><tr><th>Paid</th><th>Deduction</th><th>Hours sub.</th><th>Effective from</th><th>Effective to</th></tr></thead>
            <tbody>
              {attHistRows.map(h => (
                <tr key={h.id}>
                  <td>{h.is_paid === null ? "—" : String(h.is_paid)}</td>
                  <td>{h.deduction_type || "—"}</td>
                  <td>{h.hours_substitute ?? "—"}</td>
                  <td>{h.effective_from ? String(h.effective_from).slice(0, 10) : "—"}</td>
                  <td>{h.effective_to ? String(h.effective_to).slice(0, 10) : <span className="text-success">current</span>}</td>
                </tr>
              ))}
              {!attHistRows.length && <tr><td colSpan="5" className="text-center text-muted">No override history</td></tr>}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter><Button color="light" onClick={() => setAttHistModal(false)}>Close</Button></ModalFooter>
      </Modal>
    </div>
  )
}

export default CompanyEdit
